<script lang="ts">
  import { onMount } from 'svelte';
  import { navigate, useLocation } from 'svelte-routing';
  import { _, locale } from 'svelte-i18n';
  import {
    api,
    type Document,
    type Category,
    type PaginatedResponse,
    type Household,
    type UserTagCount,
  } from '../lib/api';
  import { localizeCategories } from '../lib/categoriesLocalization';
  import { categoriesStore } from '../lib/stores/categoriesStore';
  import { browseTreeStore } from '../lib/stores/browseTreeStore';
  import { activeHouseholdStore } from '../lib/stores/activeHouseholdStore';
  import { householdsStore } from '../lib/stores/householdsStore';
  import type { FilterValue } from '../lib/components/ui/FilterToolbar.svelte';
  import List from 'lucide-svelte/icons/list';
  import LayoutGrid from 'lucide-svelte/icons/layout-grid';
  import Grid3x3 from 'lucide-svelte/icons/grid-3x3';
  import GitBranch from 'lucide-svelte/icons/git-branch';
  import X from 'lucide-svelte/icons/x';
  import PageHeader from '../lib/components/layout/PageHeader.svelte';
  import { Button, Card, Spinner } from '../lib/components/ui/index';
  import BrowseFilterBar, { type BrowseFilters } from '../lib/components/BrowseFilterBar.svelte';
  import DocumentTable from '../lib/components/DocumentTable.svelte';
  import DocumentTiles from '../lib/components/DocumentTiles.svelte';
  import TreeView from '../lib/components/TreeView.svelte';
  import BulkReviewPanel from '../lib/components/BulkReviewPanel.svelte';
  import Modal from '../lib/components/ui/Modal.svelte';
  import {
    emptyFilters,
    filtersToQuery,
    filtersFromQuery,
    filtersToApiParams,
    clearAllFilters as clearedFilters,
  } from '../lib/browseFilters';
  import { isSelectionClearKey } from '../lib/selectionShortcut';
  import { toastStore } from '../lib/stores/toastStore';
  import { assembleBrowseBulkPayload } from '../lib/browseBulkEdit';
  import {
    isBrowseView,
    readStoredBrowseView,
    storeBrowseView,
    type BrowseView,
  } from '../lib/browseView';

  // svelte-routing v2 does NOT pass `location` as a prop to routed components
  // (Route forwards only path params + extra props). Subscribe to the router's
  // location store instead, so query-only changes on the same path — e.g. a second
  // top-bar search to /browse — are observed even though Browse is not remounted (#211).
  const location = useLocation();
  let mounted = false;

  let rawCategories: Category[] = [];
  let categories: Category[] = [];
  let documents: Document[] = [];
  let loading = true;
  let error: string | null = null;
  let paginationData: PaginatedResponse<Document>['pagination'] | null = null;

  // Presentation (Spec 120 E1): URL wins over the per-browser memory, default table.
  let view: BrowseView = 'table';
  // The list and the tree load separately; whichever is not on screen may be out of date.
  let listDirty = true;
  let treeDirty = true;
  let treeLoading = true;
  let treeError: string | null = null;

  // Filter state (Spec 120 E3, Spec 122 E1) — one object for all presentations. The household
  // scope (ADR-046) defaults to the active household; null = all households.
  let filters: BrowseFilters = emptyFilters($activeHouseholdStore ?? null);
  let userTagOptions: UserTagCount[] = [];

  // Sort & paging (table and tiles)
  let sortBy: string | null = 'uploadedAt';
  let sortOrder: 'asc' | 'desc' | null = 'desc';
  let pageSize = 20;
  let page = 0;
  let cursorStack: (string | undefined)[] = [undefined];

  // --- Multiselect + bulk edit state (Spec 009 E3, E4, E9) — table only (Spec 120 E8) ---
  let selectedIds: Set<string> = new Set();

  // Chip values (two-way bound to BulkReviewPanel); only set chips are sent (Spec 103 E6).
  let bulkHouseholdId: string = '';
  let bulkTags: string[] = [];
  let bulkAssignMemberIds: string[] = [];
  let bulkSaving = false;
  let bulkError: string | null = null;
  let bulkPartialWarning: string | null = null;
  let bulkModalOpen = false;
  // Der Papierkorb-Pfad der Dokumentenliste. Das Menue in BulkReviewPanel warf seit #295
  // ein `delete`-Event, das hier niemand fing — der Menuepunkt tat schlicht nichts (iter-29).
  let bulkDeleting = false;

  $: householdsData = ($householdsStore ?? []) as Household[];

  // Members offered for bulk assignment (Spec 009, analog Inbox) and for the person filter.
  $: bulkAssignMembers = (() => {
    const active = $householdsStore.find((h: Household) => h.id === $activeHouseholdStore);
    if (active?.members?.length)
      return active.members as Array<{ id: string; name: string; role?: string }>;
    return $householdsStore.flatMap((h: Household) => h.members ?? []) as Array<{
      id: string;
      name: string;
      role?: string;
    }>;
  })();
  // Typed cast for template (Svelte parser rejects `as any[]` inside {} bindings).
  $: bulkHouseholds = $householdsStore as Array<{ id: string; name?: string; type?: string }>;

  // Re-localize categories when locale changes
  $: if ($locale && rawCategories.length > 0) {
    categories = localizeCategories(rawCategories);
  }

  async function loadCategories() {
    try {
      rawCategories = await api.documents.getCategories();
      categories = localizeCategories(rawCategories);
    } catch {
      // handleApiError already showed toast + redirected if 401
    }
  }

  // Tags with their document counts for the tag filter, scoped to the household filter (#238).
  async function loadUserTags() {
    try {
      userTagOptions = await api.tags.getUserTags(filters.householdId ?? undefined);
    } catch {
      userTagOptions = [];
    }
  }

  function buildQuery(): string {
    const params = new URLSearchParams();
    if (view !== 'table') params.set('view', view);
    if (sortBy && sortBy !== 'uploadedAt') params.set('sortBy', sortBy);
    if (sortOrder && sortOrder !== 'desc') params.set('sortOrder', sortOrder);
    if (pageSize !== 20) params.set('pageSize', pageSize.toString());
    for (const [name, value] of filtersToQuery(filters, $activeHouseholdStore ?? null))
      params.set(name, value);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }

  function updateUrlState() {
    // Use svelte-routing navigate (not window.history.replaceState) so the router's
    // location stays in sync — otherwise a second top-bar search to /browse would not
    // update the location prop and the search watcher would not fire (#211).
    navigate(`/browse${buildQuery()}`, { replace: true });
  }

  function parseUrlState(search: string) {
    const params = new URLSearchParams(search);
    const urlView = params.get('view');
    // A URL without `view` keeps the current presentation (Topbar searches carry none).
    if (isBrowseView(urlView)) view = urlView;
    sortBy = params.get('sortBy') || 'uploadedAt';
    sortOrder = (params.get('sortOrder') as 'asc' | 'desc') || 'desc';
    pageSize = parseInt(params.get('pageSize') || '20', 10);
    // The cursor stack is not in the URL, so a restored URL always starts on the first page.
    page = 0;
    filters = filtersFromQuery(search, $activeHouseholdStore ?? null);
  }

  // Sequence guards: only the newest request may write its result (late older responses are dropped).
  let listRun = 0;
  let treeRun = 0;

  async function loadDocuments() {
    const run = ++listRun;
    loading = true;
    error = null;
    listDirty = false;

    try {
      await categoriesStore.ensureLoaded();

      const params = {
        ...filtersToApiParams(filters),
        pageSize,
        sortBy: sortBy || 'uploadedAt',
        sortOrder: sortOrder || 'desc',
        cursor: cursorStack[page],
      };

      const response = await api.documents.listDocuments(
        params as Parameters<typeof api.documents.listDocuments>[0],
      );
      if (run !== listRun) return;
      documents = response.items;
      paginationData = response.pagination;

      // Store next page cursor if we haven't seen it yet
      const nextPage = page + 1;
      if (response.pagination.nextCursor && cursorStack.length <= nextPage) {
        cursorStack = [...cursorStack, response.pagination.nextCursor];
      }
    } catch (err) {
      if (run !== listRun) return;
      // handleApiError already showed toast + redirected if 401
      error = err instanceof Error ? err.message : $_('errors.generic');
    } finally {
      if (run === listRun) loading = false;
    }
  }

  async function loadTree() {
    const run = ++treeRun;
    treeLoading = true;
    treeError = null;
    treeDirty = false;
    try {
      await browseTreeStore.loadTree(filtersToApiParams(filters));
    } catch (err) {
      if (run !== treeRun) return;
      // handleApiError already showed toast + redirected if 401
      treeError = err instanceof Error ? err.message : $_('errors.generic');
    } finally {
      if (run === treeRun) treeLoading = false;
    }
  }

  // Loads what is on screen; the other presentation's data is stale from now on.
  function loadResults() {
    if (view === 'tree') {
      listDirty = true;
      return loadTree();
    }
    treeDirty = true;
    return loadDocuments();
  }

  function resetCursorStack() {
    cursorStack = [undefined];
    page = 0;
  }

  function applyFilterChange() {
    resetCursorStack();
    updateUrlState();
    loadResults();
  }

  // --- Presentation switch (Spec 120 E1, E8) ---
  function handleViewChange(next: BrowseView) {
    if (next === view) return;
    view = next;
    storeBrowseView(next);
    if (next !== 'table') handleBulkClear();
    updateUrlState();
    if (next === 'tree' ? treeDirty : listDirty) loadResults();
  }

  // --- Multiselect handlers (Spec 009 E3) ---
  function handleSelectionChange(e: CustomEvent<{ selectedIds: string[] }>) {
    selectedIds = new Set(e.detail.selectedIds);
  }

  // --- Bulk accept handler (Spec 009 E5, E8, E9) ---
  async function handleBulkAccept() {
    const ids = [...selectedIds];
    if (ids.length === 0) return;

    bulkSaving = true;
    bulkError = null;
    bulkPartialWarning = null;

    try {
      // bulkTags is already flushed by BulkReviewPanel.acceptBulk before dispatching 'accept'
      const payload = assembleBrowseBulkPayload({
        bulkTags,
        newTag: '', // panel already merged newTag into bulkTags via mergeBulkTags
        bulkHouseholdId,
        bulkAssignMemberIds,
      });

      // E5: Only metadata update — reviewDocumentsBatch is NOT called
      const result = await api.documents.updateDocumentsMetadataBatch(ids, payload);

      // E9: Partial-success detection
      if (result.updatedCount < result.requestedCount) {
        bulkPartialWarning = $_('pages.browse.bulk_partial_warning', {
          values: { updated: result.updatedCount, total: result.requestedCount },
        });
        // modal stays open so the user sees the warning
      } else {
        bulkModalOpen = false;
      }

      // E8: Re-fetch with current filter (no localStorage)
      selectedIds = new Set();
      await loadDocuments();
    } catch (err) {
      // E9: Keep selection for retry; show error
      bulkError = err instanceof Error ? err.message : $_('pages.browse.bulk_error');
    } finally {
      bulkSaving = false;
    }
  }

  async function handleBulkDelete() {
    const ids = [...selectedIds];
    if (ids.length === 0 || bulkDeleting) return;
    bulkDeleting = true;
    try {
      const result = await api.documents.deleteDocumentsBatch(ids);
      selectedIds = new Set();
      bulkModalOpen = false;
      toastStore.success(
        $_('pages.browse.bulk_deleted_success', { values: { count: result.deletedCount } }),
        undefined,
        { action: { label: $_('messages.undo'), onClick: () => undoBulkDelete(ids) } },
      );
      await loadDocuments();
    } catch (err) {
      console.error('Bulk delete failed:', err);
      toastStore.error($_('pages.browse.bulk_delete_error'));
    } finally {
      bulkDeleting = false;
    }
  }

  // Spec 113 E2/E3: restore exactly the moved ids, then reload the list.
  async function undoBulkDelete(ids: string[]) {
    try {
      await api.documents.restoreDocuments(ids);
      treeDirty = true;
      await loadResults();
    } catch (err) {
      console.error('Restore failed:', err);
      toastStore.error(
        $_(ids.length === 1 ? 'messages.undo_failed' : 'messages.undo_failed_plural'),
      );
    }
  }

  // Spec 103 E9: Esc clears the (cross-page) selection — not while a modal is open (it closes
  // first; Modal listens on `document`, so this capture handler still sees it open), not from
  // a text field.
  function handleWindowKeydown(e: KeyboardEvent) {
    if (!bulkModalOpen && selectedIds.size > 0 && isSelectionClearKey(e)) handleBulkClear();
  }

  // Spec 103 E12: a new selection starts with an empty editor.
  let prevSelectionSize = 0;
  $: {
    if (selectedIds.size > 0 && prevSelectionSize === 0) {
      bulkAssignMemberIds = [];
      bulkHouseholdId = '';
      bulkTags = [];
    }
    prevSelectionSize = selectedIds.size;
  }

  function handleBulkClear() {
    selectedIds = new Set();
    bulkError = null;
    bulkPartialWarning = null;
    bulkModalOpen = false;
  }

  function handleSort(e: CustomEvent<{ key: string; order: 'asc' | 'desc' | null }>) {
    sortBy = e.detail.order ? e.detail.key : null;
    sortOrder = e.detail.order;
    resetCursorStack();
    updateUrlState();
    loadDocuments();
  }

  const firstOf = (value: FilterValue): string | null =>
    Array.isArray(value) && value.length > 0 ? value[0] : null;

  // What each toolbar key changes in the filter object.
  const FILTER_PATCHES: Record<string, (value: FilterValue) => Partial<BrowseFilters>> = {
    search: (v) => ({ search: typeof v === 'string' ? v : null }),
    fileFormat: (v) => ({ fileFormat: firstOf(v) }),
    fileSize: (v) => {
      const range = v as { min?: number | null; max?: number | null } | null;
      return { minSize: range?.min ?? null, maxSize: range?.max ?? null };
    },
    dateRange: (v) => {
      const range = v as { from?: string | null; to?: string | null } | null;
      return { fromDate: range?.from ?? null, toDate: range?.to ?? null };
    },
    category: (v) => ({ category: firstOf(v), subCategory: null }),
    subCategory: (v) => ({ subCategory: firstOf(v) }),
    reviewStatus: (v) => {
      const status = firstOf(v);
      return { reviewStatus: status === 'unreviewed' || status === 'reviewed' ? status : null };
    },
    assignedTo: (v) => ({ assignedTo: firstOf(v) }),
    userTag: (v) => ({ userTag: firstOf(v) }),
    household: (v) => ({ householdId: firstOf(v) }),
  };

  function handleFilter(e: CustomEvent<{ key: string; value: FilterValue }>) {
    const { key, value } = e.detail;
    filters = { ...filters, ...FILTER_PATCHES[key]?.(value) };
    if (key === 'household') loadUserTags();
    applyFilterChange();
  }

  function handlePageChange(e: CustomEvent<number>) {
    page = e.detail;
    updateUrlState();
    loadDocuments();
  }

  function handlePageSizeChange(e: CustomEvent<number>) {
    pageSize = e.detail;
    applyFilterChange();
  }

  function clearAllFilters() {
    filters = clearedFilters(filters);
    applyFilterChange();
  }

  // When the active household changes in the topbar, reset the household filter
  // to the new household (the user explicitly switched scope).
  let prevHouseholdId = $activeHouseholdStore;
  $: if ($activeHouseholdStore !== prevHouseholdId) {
    prevHouseholdId = $activeHouseholdStore;
    // Follow the topbar selection: update the visible filter chip.
    filters = { ...filters, householdId: $activeHouseholdStore ?? null };
    updateUrlState();
    resetCursorStack();
    loadResults();
    loadUserTags();
  }

  // React to URL changes after mount — a second top-bar search to /browse changes
  // only the query string, so Browse is NOT remounted and onMount does not re-run.
  // The router location store keeps filter state + results in sync (#211). The
  // `mounted` guard prevents a double initial load (onMount does the first load).
  //
  // Guard on the location search string (prevLocationSearch), NOT on the filter
  // variables: this reactive block would otherwise re-run whenever a filter changes while
  // $location still holds the PRE-navigate URL, and reset the just-set filter (#239). Only act
  // when the URL's query string itself changed (top-bar search, back/forward) and differs from
  // what this page would write for its own state (its own navigate() is a no-op here).
  let prevLocationSearch: string | null = null;
  $: if (mounted && $location && $location.search !== prevLocationSearch) {
    prevLocationSearch = $location.search;
    if ($location.search !== buildQuery()) {
      const leavingTable = view === 'table';
      parseUrlState($location.search);
      if (leavingTable && view !== 'table') handleBulkClear();
      resetCursorStack();
      loadResults();
    }
  }

  onMount(() => {
    // The URL wins over the per-browser memory (Spec 120 E1, E11).
    view = readStoredBrowseView();
    parseUrlState(window.location.search);
    // Seed the location guard with the current URL so the first reactive run after
    // mounted=true is a no-op (onMount already did the initial load). See #239.
    prevLocationSearch = window.location.search;
    loadCategories();
    loadUserTags();
    loadResults();
    mounted = true;
  });

  const VIEW_OPTIONS = [
    { view: 'table', icon: List, labelKey: 'pages.browse.view_table' },
    { view: 'tiles-large', icon: LayoutGrid, labelKey: 'pages.browse.view_tiles_large' },
    { view: 'tiles-small', icon: Grid3x3, labelKey: 'pages.browse.view_tiles_small' },
    { view: 'tree', icon: GitBranch, labelKey: 'pages.browse.view_tree' },
  ] as const;
</script>

<svelte:window on:keydown|capture={handleWindowKeydown} />

<PageHeader title={$_('pages.browse.title')}>
  <svelte:fragment slot="actions">
    <div
      role="group"
      aria-label={$_('pages.browse.view_mode_label')}
      class="flex items-center gap-1 bg-surface border border-border rounded-lg overflow-hidden"
    >
      {#each VIEW_OPTIONS as option (option.view)}
        <button
          type="button"
          data-testid="browse-viewmode-{option.view}"
          aria-pressed={view === option.view}
          title={$_(option.labelKey)}
          class="px-3 py-1.5 text-sm font-medium transition-colors
            {view === option.view
            ? 'bg-accent text-on-accent'
            : 'text-text-secondary hover:text-text hover:bg-surface-hover'}"
          on:click={() => handleViewChange(option.view)}
        >
          <span class="flex items-center gap-1.5">
            <svelte:component this={option.icon} class="w-4 h-4" strokeWidth={1.5} />
            <span class="hidden md:inline">{$_(option.labelKey)}</span>
          </span>
        </button>
      {/each}
    </div>
  </svelte:fragment>
</PageHeader>

<!-- One filter bar for all presentations; stays visible on empty results (Spec 120 E3). -->
<div class="mb-4 bg-surface border border-border rounded-lg [&>div]:border-b-0">
  <BrowseFilterBar
    {filters}
    {categories}
    userTags={userTagOptions}
    members={bulkAssignMembers}
    households={householdsData}
    on:filter={handleFilter}
    on:clearAll={clearAllFilters}
  />
</div>

{#if view === 'tree'}
  {#if treeError}
    <Card>
      <div class="text-center py-8">
        <p class="text-sm text-error mb-3">{treeError}</p>
        <Button variant="primary" size="sm" on:click={() => loadTree()}
          >{$_('pages.home.retry')}</Button
        >
      </div>
    </Card>
  {:else if treeLoading}
    <div data-testid="browse-loading" class="flex justify-center py-12">
      <Spinner size="lg" label={$_('messages.loading')} />
    </div>
  {:else}
    <div data-testid="browse-treeview">
      <TreeView />
    </div>
  {/if}
{:else if error}
  <Card>
    <div class="text-center py-8">
      <p class="text-sm text-error mb-3">{error}</p>
      <Button variant="primary" size="sm" on:click={() => loadDocuments()}
        >{$_('pages.home.retry')}</Button
      >
    </div>
  </Card>
{:else if view === 'table'}
  <DocumentTable
    {documents}
    showFilters={false}
    {loading}
    {page}
    {pageSize}
    {sortBy}
    {sortOrder}
    {selectedIds}
    hasMore={paginationData?.hasMore || false}
    hasPrev={page > 0}
    on:sort={handleSort}
    on:pageChange={handlePageChange}
    on:pageSizeChange={handlePageSizeChange}
    on:selectionChange={handleSelectionChange}
  />
{:else}
  <DocumentTiles
    {documents}
    {loading}
    {page}
    {pageSize}
    size={view === 'tiles-large' ? 'large' : 'small'}
    hasMore={paginationData?.hasMore || false}
    hasPrev={page > 0}
    on:pageChange={handlePageChange}
    on:pageSizeChange={handlePageSizeChange}
  />
{/if}

{#if view === 'table'}
  <!-- Floating selection bar: fixed to viewport bottom, no layout shift (Spec 009) -->
  {#if selectedIds.size > 0}
    <div
      data-testid="bulk-selection-bar"
      class="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-2.5 bg-surface border border-border rounded-full shadow-lg"
    >
      <span class="text-sm font-medium text-text whitespace-nowrap">
        {$_('pages.browse.bulk_bar_selected', { values: { count: selectedIds.size } })}
      </span>
      <button
        type="button"
        on:click={handleBulkClear}
        aria-label={$_('pages.browse.bulk_bar_clear')}
        title={$_('pages.browse.bulk_bar_clear')}
        class="-ml-2 -my-1 p-2.5 text-text-muted hover:text-text hover:bg-surface-hover rounded-full transition-colors"
      >
        <X class="w-3.5 h-3.5" strokeWidth={1.5} />
      </button>
      <button
        type="button"
        data-testid="bulk-bar-edit"
        on:click={() => (bulkModalOpen = true)}
        class="flex items-center gap-2 px-3 py-1.5 bg-accent text-on-accent rounded-full text-sm font-semibold hover:bg-accent-hover transition-colors whitespace-nowrap"
      >
        {$_('pages.browse.bulk_bar_edit', { values: { count: selectedIds.size } })}
      </button>
    </div>
  {/if}

  <!-- Bulk edit modal (Spec 009) -->
  <Modal
    open={bulkModalOpen}
    title={$_('pages.browse.bulk_modal_title')}
    on:close={() => (bulkModalOpen = false)}
  >
    {#if bulkError}
      <div class="mb-3 px-3 py-2 bg-error/10 border border-error/30 rounded-lg text-sm text-error">
        {bulkError}
      </div>
    {/if}
    {#if bulkPartialWarning}
      <div
        class="mb-3 px-3 py-2 bg-warning/10 border border-warning/30 rounded-lg text-sm text-warning"
      >
        {bulkPartialWarning}
      </div>
    {/if}
    <div class="max-h-[65vh] overflow-y-auto -mx-5 px-5">
      <BulkReviewPanel
        count={selectedIds.size}
        members={bulkAssignMembers}
        households={bulkHouseholds}
        mode="edit"
        bind:bulkHouseholdId
        bind:bulkTags
        bind:bulkAssignMemberIds
        saving={bulkSaving}
        on:accept={handleBulkAccept}
        on:delete={handleBulkDelete}
      />
    </div>
  </Modal>
{/if}
